/**
 * @description
 * 获取实名验证链接（需要先获得微信支付实名验证信息，App store端会进行验证）
 */

import { ref } from "@vue/composition-api"
import * as config from "../config"
import { createNamespacedHelpers } from "vuex-composition-helpers"
import MUTATION_TYPES from "../store/apple-gift-card/mutation-types"
import { AppleGiftCardActions } from "../store/apple-gift-card/actions"
import { AppleGiftCardGetters } from "../store/apple-gift-card/getters"
import { AppleGiftCardMutations } from "../store/apple-gift-card/mutations"
import { AppleGiftCardState } from "../store/apple-gift-card/state"
import { stringify } from "querystring"

const { useActions, useMutations } = createNamespacedHelpers<
  AppleGiftCardState,
  AppleGiftCardGetters,
  AppleGiftCardActions,
  AppleGiftCardMutations
>("appleGiftCard")

export const enum PopupType {
  IS_APPLE_AUTH_SHOWN = "IS_APPLE_AUTH_SHOWN",
  IS_PHONE_INFO_SHOWN = "IS_PHONE_INFO_SHOWN",
  IS_APPLE_AUTH_ERROR_SHOWN = "IS_APPLE_AUTH_ERROR_SHOWN",
  CLOSE = "CLOSE"
}

// eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
export default () => {
  const { reportUserAction, getAppleSignUrl, setToastMsg, getWechatRealnameInfo } = useActions([
    "reportUserAction",
    "getAppleSignUrl",
    "setToastMsg",
    "getWechatRealnameInfo"
  ])
  const mutations = useMutations([MUTATION_TYPES.SET_IS_LOADING])

  const popupStatus = ref(PopupType.CLOSE)
  const appleAuthErrorMsg = ref("")
  const appleAuthRetry = ref(0)

  const phoneInputTimer = ref<number | null>(null)
  const isPhoneNumValid = ref(true)
  const phoneNum = ref("")

  const changePhoneNum = (e: any) => {
    if (phoneInputTimer.value !== null) {
      window.clearTimeout(phoneInputTimer.value)
      phoneInputTimer.value = null
    }
    const value = e?.target?.value?.replace(/^0/, "").replace(/\D*/gi, "")
    phoneNum.value = value
    phoneInputTimer.value = window.setTimeout(
      (phone: string) => {
        if (phone && /^[1]([3-9])[0-9]{9}$/.test(phone)) {
          isPhoneNumValid.value = true
        } else {
          isPhoneNumValid.value = false
        }
        reportUserAction({
          iformat: config.REPORT.CLICK.PHONE_INPUT
        })
      },
      1500,
      value
    )
  }

  const closePopUp = () => (popupStatus.value = PopupType.CLOSE)

  const showAppleAuth = async () => {
    reportUserAction({
      iformat: config.REPORT.CLICK.APPLE_AUTH
    })
    await getWechatRealnameInfo()
    popupStatus.value = PopupType.IS_PHONE_INFO_SHOWN
  }

  /**
   * @description
   * 用于获取签约链接并跳转
   *
   * 获取签约链接需要微信实名认证信息（App Store端会进行验证）
   * 所以要确保调用这个函数前调用了getWechatRealnameInfo
   */
  const goAppleAuth = async (mobile: string) => {
    reportUserAction({
      iformat: config.REPORT.CLICK.PHONE_CONFIRM_AUTH
    })
    if (!mobile) {
      setToastMsg({ toastMsg: "请输入手机号码" })
      return
    }
    if (!/^[1]([3-9])[0-9]{9}$/.test(mobile)) {
      setToastMsg({ toastMsg: "请输入正确的手机号码" })
      return
    }
    mutations[MUTATION_TYPES.SET_IS_LOADING](true)
    try {
      const res = await getAppleSignUrl({ mobile })
      location.href = res.info.contract_url
    } catch (e: any) {
      if (e && e.msg && e.err_code == "") {
        appleAuthErrorMsg.value = e.msg
      } else {
        appleAuthErrorMsg.value = "因网络中断或其他异常，实名信息认证失败，请稍后重试。"
      }
      popupStatus.value = PopupType.IS_APPLE_AUTH_ERROR_SHOWN
      reportUserAction({
        iformat: config.REPORT.PAGEVIEW.APPLE_AUTH_ERROR,
        resultInfo: e
      })
    }
    mutations[MUTATION_TYPES.SET_IS_LOADING](false)
  }

  const retryAppleAuth = () => {
    reportUserAction({
      iformat: config.REPORT.CLICK.APPLE_AUTH_RETRY
    })
    goAppleAuth(phoneNum.value)
    appleAuthRetry.value += 1
  }

  return {
    phoneNum,
    isPhoneNumValid,
    appleAuthErrorMsg,
    appleAuthRetry,
    popupStatus,
    closePopUp,
    changePhoneNum,
    showAppleAuth,
    goAppleAuth,
    retryAppleAuth
  }
}
